<template>
  <!-- Main Post Inspector Component -->

  <div class="h-full flex flex-col bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
    <!-- Loading State -->
    <div v-if="inspector.loading.value" class="flex-grow flex items-center justify-center py-12 min-h-[calc(100vh-200px)]">
      <div class="flex flex-col items-center justify-center">
        <VLoading show-loading-text :loading-text="t('common.loading')"/>
      </div>
    </div>

    <!-- Content Not Found State -->
    <div v-else-if="!inspector.content.value" class="flex-grow flex items-center justify-center py-12">
      <EmptyState icon="error_outline" :message="t('socialMedia.components.postInspector.noContent')"/>
    </div>

    <template v-else>
      <!-- Platform and Status Information -->
      <PostInfo 
        :platformName="inspector.platformName.value" 
        :date="inspector.content.value.publishedDate || inspector.content.value.addDate" 
        :isPublished="inspector.content.value.isPublished"
      />

      <!-- Main Content Area -->
      <div class="flex-grow overflow-y-auto">
        <!-- Content Section -->
        <PostContent 
          :message="inspector.content.value.message" 
          :hashtags="inspector.parsedHashtags.value" 
          :link="inspector.content.value.link" 
          :linkPreview="inspector.linkPreview.value" 
          :isImageLink="inspector.isImageLink.value" 
          :contentId="postId"
          @visit-link="inspector.openExternalLink"
          @suggestion-created="handleSuggestionCreated"
        />

        <!-- Statistics -->
        <PostStats 
          :likes="inspector.content.value.postLikes" 
          :comments="inspector.commentCount.value" 
          :shares="inspector.content.value.shares || 0"
        />

        <!-- Comments Section -->
        <PostComments 
          :postId="postId" 
          @comments-loaded="inspector.updateCommentCount"
        />
      </div>

      <!-- Footer -->
      <PostFooter 
        :date="inspector.content.value.publishedDate || inspector.content.value.addDate" 
        :isPublished="inspector.content.value.isPublished" 
        :isPublishing="inspector.isContentPublishing.value" 
        @publish="inspector.publishPost" 
        @analytics="inspector.navigateToAnalytics"
      />
    </template>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useAssetStore } from '@/stores/asset'
import { usePostInspector } from '../../../../composables'
import { ref, nextTick, onMounted, watch } from 'vue'
import { responseHandlerService } from '@/plugins/responseHandlerService'

// Components
import PostInfo from './components/PostInfo.vue'
import PostContent from './components/PostContent.vue'
import PostStats from './components/PostStats.vue'
import PostComments from './components/PostComments.vue'
import PostFooter from './components/PostFooter.vue'
import VLoading from '@/components/loading/VLoading.vue'
import EmptyState from './components/EmptyState.vue'

const { t } = useI18n()
const assetStore = useAssetStore()

const props = defineProps({
  postId: {
    type: [String, Number],
    required: true,
  },
  commentId: {
    type: [String, Number],
    default: null,
  },
})

const emit = defineEmits(['update:isPublished', 'close'])

// Initialize the post inspector composable
const inspector = usePostInspector({
  postId: props.postId,
  commentId: props.commentId,
  assetId: assetStore.assetId,
  emit: emit,
})

// Force component update on mount
onMounted(async () => {
  try {
    // Explicitly manipulate loading state
    inspector.loading.value = true
    
    // Fetch content with a small delay to ensure UI updates
    setTimeout(async () => {
      try {
        await inspector.fetchContent(props.postId)
      } catch (error) {
        console.error('Error fetching content:', error)
      } finally {
        // Explicitly set loading to false
        inspector.loading.value = false
      }
    }, 100)
  } catch (error) {
    console.error('Error in onMounted:', error)
    inspector.loading.value = false
  }
})

// Öneri oluşturulduğunda tetiklenecek fonksiyon
const handleSuggestionCreated = (suggestion) => {
  responseHandlerService.handleSuccess('Öneri başarıyla oluşturuldu')
}

// Watch for changes in postId
watch(() => props.postId, async (newPostId) => {
  if (newPostId) {
    try {
      // Explicitly manipulate loading state
      inspector.loading.value = true
      
      // Fetch with small delay to ensure UI updates
      setTimeout(async () => {
        try {
          await inspector.fetchContent(newPostId)
        } catch (error) {
          console.error('Error fetching content:', error)
        } finally {
          // Explicitly set loading to false
          inspector.loading.value = false
        }
      }, 100)
    } catch (error) {
      console.error('Error in watch:', error)
      inspector.loading.value = false
    }
  }
})
</script>